import { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { withEnv } from "@/lib/three";

const clamp = (v) => Math.min(1, Math.max(0, v));
const M = {
  boardA: new THREE.MeshStandardMaterial({ color: "#ECEAE5", roughness: 0.65 }),
  boardB: new THREE.MeshStandardMaterial({ color: "#DCDAD4", roughness: 0.65 }),
  frame: new THREE.MeshStandardMaterial({ color: "#A9A9A9", roughness: 0.8 }),
  dark: new THREE.MeshStandardMaterial({ color: "#262626", roughness: 0.6 }),
  cushion: new THREE.MeshStandardMaterial({ color: "#3C3C3C", roughness: 0.95 }),
  shrub: new THREE.MeshStandardMaterial({ color: "#5E5E5E", roughness: 1 }),
  ground: new THREE.MeshStandardMaterial({ color: "#161616", roughness: 1 }),
  stone: new THREE.MeshStandardMaterial({ color: "#8E8E8E", roughness: 0.9 }),
  shingle: new THREE.MeshStandardMaterial({ color: "#2B2B2B", roughness: 0.85 }),
  leaf: new THREE.MeshStandardMaterial({ color: "#555555", roughness: 1, flatShading: true }),
  bulb: new THREE.MeshStandardMaterial({ color: "#FFFFFF", emissive: "#FFF1D6", emissiveIntensity: 0 }),
  wire: new THREE.LineBasicMaterial({ color: "#555555" }),
};

export const STAGES = [
  [0, "Setting footings"], [0.7, "Framing the platform"], [1.1, "Laying the decking"],
  [2.5, "Stairs & cable railing"], [3.0, "Covering the porch"], [3.9, "Furnishing"], [4.5, "Lights on. Ready for Friday night."],
];

// Animates its children into place starting at time `at`
const Build = ({ at, dur = 0.7, mode = "drop", position = [0, 0, 0], children }) => {
  const g = useRef();
  useFrame(({ clock }) => {
    const p = clamp((clock.elapsedTime - at) / dur);
    const e = 1 - Math.pow(1 - p, 3);
    g.current.visible = p > 0;
    if (mode === "drop") g.current.position.y = position[1] + (1 - e) * 2.6;
    else if (mode === "rise") g.current.scale.y = Math.max(0.001, e);
    else g.current.scale.setScalar(Math.max(0.001, e));
  });
  return <group ref={g} position={position}>{children}</group>;
};

const B = ({ s, p = [0, 0, 0], m = M.boardA, r }) => (
  <mesh position={p} rotation={r} material={m} castShadow receiveShadow><boxGeometry args={s} /></mesh>
);

const RailSegment = ({ a, b, at }) => {
  const along = a[0] !== b[0] ? "x" : "z";
  const len = Math.abs(along === "x" ? b[0] - a[0] : b[2] - a[2]);
  const c = [(a[0] + b[0]) / 2, 0, (a[2] + b[2]) / 2];
  const s = (h, t) => (along === "x" ? [len, h, t] : [t, h, len]);
  return (
    <Build at={at} dur={0.5} mode="pop" position={[c[0], 0, c[2]]}>
      <B s={s(0.06, 0.16)} p={[0, 2.28, 0]} m={M.boardA} />
      {[1.58, 1.82, 2.06].map((y) => <B key={y} s={s(0.02, 0.02)} p={[0, y, 0]} m={M.dark} />)}
    </Build>
  );
};

const StringLights = ({ from, to, n = 14 }) => {
  const pts = useMemo(() => Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    const v = new THREE.Vector3().lerpVectors(new THREE.Vector3(...from), new THREE.Vector3(...to), t);
    v.y -= Math.sin(Math.PI * t) * 0.32;
    return v;
  }), [from, to, n]);
  const line = useMemo(() => new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), M.wire), [pts]);
  return (
    <group>
      <primitive object={line} />
      {pts.map((v, i) => (
        <mesh key={i} position={[v.x, v.y - 0.07, v.z]} material={M.bulb}><sphereGeometry args={[0.055, 10, 10]} /></mesh>
      ))}
    </group>
  );
};

const Deck = () => {
  const posts = [-3.8, 0, 3.8].flatMap((x) => [-2.8, 0, 2.8].map((z) => [x, z]));
  const boards = Array.from({ length: 20 }, (_, i) => -2.85 + i * 0.3);
  const railPosts = [[-4, -3], [-2, -3], [0, -3], [2, -3], [4, -3], [-4, -1], [-4, 1], [4, -1], [4, 1], [-4, 3], [-2, 3], [0, 3], [1.8, 3], [3.4, 3], [4, 3]];
  const gable = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-2.1, 0); s.lineTo(2.1, 0); s.lineTo(0, 1.15); s.closePath();
    const g = new THREE.ExtrudeGeometry(s, { depth: 0.08, bevelEnabled: false });
    g.rotateY(Math.PI / 2);
    return g;
  }, []);
  const pitch = Math.atan(1.15 / 2.1);
  return (
    <group>
      {posts.map(([x, z], i) => (
        <Build key={`p${i}`} at={0.15 + i * 0.05} mode="rise" position={[x, 0, z]}><B s={[0.24, 1.2, 0.24]} p={[0, 0.6, 0]} m={M.frame} /></Build>
      ))}
      {[-2.8, 0, 2.8].map((z, i) => <Build key={`b${z}`} at={0.7 + i * 0.08} position={[0, 1.08, z]}><B s={[8, 0.22, 0.18]} m={M.frame} /></Build>)}
      <Build at={0.95} position={[0, 1.12, 0]}>
        <B s={[8.2, 0.24, 0.06]} p={[0, 0, 3.04]} m={M.dark} /><B s={[8.2, 0.24, 0.06]} p={[0, 0, -3.04]} m={M.dark} />
        <B s={[0.06, 0.24, 6.1]} p={[4.1, 0, 0]} m={M.dark} /><B s={[0.06, 0.24, 6.1]} p={[-4.1, 0, 0]} m={M.dark} />
      </Build>
      {boards.map((z, i) => (
        <Build key={`d${i}`} at={1.1 + i * 0.065} dur={0.55} position={[0, 1.28, z]}><B s={[8.2, 0.07, 0.27]} m={i % 2 ? M.boardB : M.boardA} /></Build>
      ))}
      {[1.04, 0.78, 0.52, 0.26].map((y, i) => (
        <Build key={`s${i}`} at={2.5 + i * 0.08} position={[2.6, 0, 3.22 + i * 0.34]}><B s={[1.6, y, 0.34]} p={[0, y / 2, 0]} m={i % 2 ? M.boardB : M.boardA} /></Build>
      ))}
      {railPosts.map(([x, z], i) => (
        <Build key={`r${i}`} at={2.6 + i * 0.025} mode="rise" position={[x, 1.31, z]}><B s={[0.1, 0.95, 0.1]} p={[0, 0.475, 0]} m={M.dark} /></Build>
      ))}
      <RailSegment a={[-4, 0, -3]} b={[4, 0, -3]} at={2.85} />
      <RailSegment a={[-4, 0, -3]} b={[-4, 0, 3]} at={2.9} />
      <RailSegment a={[4, 0, -3]} b={[4, 0, 3]} at={2.95} />
      <RailSegment a={[-4, 0, 3]} b={[1.8, 0, 3]} at={3.0} />
      <RailSegment a={[3.4, 0, 3]} b={[4, 0, 3]} at={3.0} />
      {[[-3.8, -2.8], [-0.2, -2.8], [-3.8, 0.4], [-0.2, 0.4]].map(([x, z], i) => (
        <Build key={`pg${i}`} at={3.0 + i * 0.07} mode="rise" position={[x, 1.31, z]}><B s={[0.22, 2.95, 0.22]} p={[0, 1.475, 0]} m={M.boardB} /></Build>
      ))}
      {[-2.8, 0.4].map((z, i) => <Build key={`pb${z}`} at={3.35 + i * 0.08} position={[-2, 4.3, z]}><B s={[4.3, 0.26, 0.14]} m={M.boardA} /></Build>)}
      <Build at={3.5} position={[-2, 4.44, -1.2]}><B s={[4.1, 0.05, 3.6]} m={M.boardB} /></Build>
      {[-4.05, 0.05].map((x, i) => (
        <Build key={`g${x}`} at={3.55 + i * 0.05} position={[x, 4.43, -1.2]}><mesh geometry={gable} material={M.boardA} castShadow /></Build>
      ))}
      {[1, -1].map((side, i) => (
        <Build key={`roof${side}`} at={3.65 + i * 0.1} position={[-2, 5.04, -1.2 + side * 1.08]}>
          <B s={[4.7, 0.12, 2.65]} r={[side * pitch, 0, 0]} m={M.shingle} />
        </Build>
      ))}
      <Build at={3.9} mode="pop" position={[-2, 4.0, -1.2]}><Fan /></Build>
    </group>
  );
};

const Fan = () => {
  const blades = useRef();
  useFrame((_, dt) => { blades.current.rotation.y += dt * 2.4; });
  return (
    <group>
      <B s={[0.04, 0.4, 0.04]} p={[0, 0.25, 0]} m={M.dark} />
      <mesh material={M.bulb}><sphereGeometry args={[0.13, 16, 16]} /></mesh>
      <group ref={blades} position={[0, 0.06, 0]}>
        {[0, 1, 2, 3].map((k) => <B key={k} s={[1.1, 0.02, 0.18]} p={[Math.cos((k * Math.PI) / 2) * 0.6, 0, Math.sin((k * Math.PI) / 2) * 0.6]} r={[0, -(k * Math.PI) / 2, 0]} m={M.dark} />)}
      </group>
    </group>
  );
};

const Tree = ({ p, s = 1, at }) => (
  <Build at={at} mode="pop" position={p}>
    <group scale={s}>
      <mesh position={[0, 0.5, 0]} material={M.dark} castShadow><cylinderGeometry args={[0.1, 0.14, 1, 8]} /></mesh>
      {[[1.0, 1.3, 1.6], [1.8, 1.0, 1.3], [2.5, 0.7, 1.0]].map(([y, r, h]) => (
        <mesh key={y} position={[0, y, 0]} material={M.leaf} castShadow><coneGeometry args={[r, h, 7]} /></mesh>
      ))}
    </group>
  </Build>
);

const Fireflies = ({ count = 70 }) => {
  const ref = useRef();
  const { geo, base } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) { pos[i * 3] = (Math.random() - 0.5) * 13; pos[i * 3 + 1] = 0.5 + Math.random() * 5; pos[i * 3 + 2] = (Math.random() - 0.5) * 11 + 0.9; }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos.slice(), 3));
    return { geo: g, base: pos };
  }, [count]);
  const mat = useMemo(() => new THREE.PointsMaterial({ color: "#FFF1D6", size: 0.07, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }), []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const a = geo.attributes.position;
    for (let i = 0; i < count; i++) {
      a.array[i * 3] = base[i * 3] + Math.sin(t * 0.4 + i) * 0.3;
      a.array[i * 3 + 1] = base[i * 3 + 1] + Math.sin(t * 0.7 + i * 1.7) * 0.25;
    }
    a.needsUpdate = true;
    mat.opacity = clamp((t - 4.6) / 1.5) * (0.65 + Math.sin(t * 2) * 0.2);
  });
  return <points ref={ref} geometry={geo} material={mat} />;
};

const Furniture = () => (
  <group>
    <Build at={3.9} mode="pop" position={[-2, 1.31, -2.1]}>
      <B s={[2.6, 0.34, 0.9]} p={[0, 0.17, 0]} m={M.cushion} /><B s={[2.6, 0.55, 0.2]} p={[0, 0.5, -0.4]} m={M.cushion} />
      <B s={[0.18, 0.5, 0.9]} p={[1.3, 0.25, 0]} m={M.cushion} /><B s={[0.18, 0.5, 0.9]} p={[-1.3, 0.25, 0]} m={M.cushion} />
    </Build>
    <Build at={4.0} mode="pop" position={[-2, 1.31, -0.9]}><B s={[1.2, 0.3, 0.65]} p={[0, 0.15, 0]} m={M.frame} /></Build>
    {[1.9, 3.1].map((x, i) => (
      <Build key={x} at={4.05 + i * 0.08} mode="pop" position={[x, 1.31, -2.1]}>
        <B s={[0.75, 0.3, 0.75]} p={[0, 0.15, 0]} m={M.cushion} /><B s={[0.75, 0.55, 0.14]} p={[0, 0.48, -0.36]} r={[-0.25, 0, 0]} m={M.cushion} />
      </Build>
    ))}
    <Build at={4.2} mode="pop" position={[2.5, 1.31, -0.6]}>
      <mesh position={[0, 0.2, 0]} material={M.dark} castShadow><cylinderGeometry args={[0.5, 0.5, 0.4, 28]} /></mesh>
      <mesh position={[0, 0.41, 0]} rotation={[-Math.PI / 2, 0, 0]} material={M.bulb}><ringGeometry args={[0.12, 0.32, 28]} /></mesh>
    </Build>
    <Build at={4.25} mode="pop" position={[3.4, 1.31, 2.1]}>
      <B s={[0.6, 0.6, 0.6]} p={[0, 0.3, 0]} m={M.dark} />
      <mesh position={[0, 0.95, 0]} material={M.shrub} castShadow><icosahedronGeometry args={[0.5, 1]} /></mesh>
    </Build>
  </group>
);

const Island = () => {
  const geo = useMemo(() => new THREE.BoxGeometry(13, 0.8, 11), []);
  const edges = useMemo(() => new THREE.EdgesGeometry(geo), [geo]);
  return (
    <group position={[0, -0.4, 0.9]}>
      <mesh geometry={geo} material={M.ground} receiveShadow />
      <lineSegments geometry={edges}><lineBasicMaterial color="#FFFFFF" transparent opacity={0.22} /></lineSegments>
      {[4.6, 5.3].map((z, i) => <B key={z} s={[1.1, 0.06, 0.5]} p={[1.7 + i * 0.2, 0.43, z]} m={M.stone} />)}
    </group>
  );
};

const Lights = ({ onStage }) => {
  const warm = useRef();
  const last = useRef(-1);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const on = clamp((t - 4.5) / 0.8);
    M.bulb.emissiveIntensity = on * (2.2 + Math.sin(t * 3) * 0.08);
    warm.current.intensity = on * 14;
    const idx = STAGES.reduce((acc, [s], i) => (t >= s ? i : acc), 0);
    if (idx !== last.current) { last.current = idx; onStage?.(idx); }
  });
  return <pointLight ref={warm} position={[-2, 3.7, -1.2]} color="#FFE6BF" intensity={0} distance={12} decay={1.6} />;
};

const Rig = ({ children, progress }) => {
  const g = useRef();
  const mouse = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const f = (e) => { mouse.current = { x: (e.clientX / window.innerWidth) * 2 - 1, y: (e.clientY / window.innerHeight) * 2 - 1 }; };
    window.addEventListener("pointermove", f);
    return () => window.removeEventListener("pointermove", f);
  }, []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const s = progress?.get() || 0;
    const ty = -0.2 + mouse.current.x * 0.35 + Math.sin(t * 0.15) * 0.12 + s * 0.9;
    g.current.rotation.y = THREE.MathUtils.lerp(g.current.rotation.y, ty, 0.04);
    g.current.rotation.x = THREE.MathUtils.lerp(g.current.rotation.x, mouse.current.y * 0.04, 0.04);
    g.current.position.y = Math.sin(t * 0.6) * 0.08;
  });
  return <group ref={g}>{children}</group>;
};

export const DeckHero = ({ progress, onStage, className = "", testId }) => (
  <div className={className} data-testid={testId}>
    <Canvas shadows dpr={[1, 1.75]} camera={{ position: [12, 9, 15.5], fov: 30 }} onCreated={(st) => { st.camera.lookAt(0, 2.3, 0.6); withEnv(0.25)(st); }}>
      <color attach="background" args={["#0A0A0A"]} />
      <fog attach="fog" args={["#0A0A0A", 18, 34]} />
      <ambientLight intensity={0.3} />
      <spotLight position={[6, 14, 8]} angle={0.5} penumbra={0.9} intensity={420} castShadow shadow-mapSize={[2048, 2048]} shadow-bias={-0.0004} />
      <directionalLight position={[-8, 5, -6]} intensity={0.7} />
      <Rig progress={progress}>
        <Island />
        <Deck />
        <Furniture />
        <Tree p={[-5.4, 0, -3.6]} s={1.25} at={4.0} />
        <Tree p={[5.5, 0, -3.8]} s={1} at={4.1} />
        <Tree p={[-5.6, 0, 3.6]} s={0.85} at={4.2} />
        <Tree p={[5.6, 0, 4.6]} s={0.7} at={4.3} />
        <Fireflies />
        <StringLights from={[-0.2, 4.4, 0.4]} to={[4, 2.35, 3]} />
        <StringLights from={[-0.2, 4.4, -2.8]} to={[4, 2.35, -3]} />
        <Lights onStage={onStage} />
      </Rig>
    </Canvas>
  </div>
);

export default DeckHero;
